import { useTranslations } from '@/i18n/use-translations';
import { createEventSchemaFactory } from '@/schemas/event/create-event';
import { browserTimeZone } from '@/utils/time-zone';
import useCreateEventMutation from '@/state/mutations/events/use-create-event.ts';
import { useActiveOrganizationStore } from '@/state/stores/use-organization-store';
import { EVENT_TYPE, type EventType } from '@/types/dtos/events/event-type.enum';
import {
  EVENT_CONTRIBUTORS_ROLES,
  type EventContributorRole,
} from '@/types/dtos/contributors/response/contributor.dto';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Switch,
  Stack,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { useNotifications } from '@/hooks/use-notifications';
import { Controller, useForm, useWatch } from 'react-hook-form';
import type { z } from 'zod';
import Modal from '@/components/common/modal';
import EventTypeInfo from '@/components/common/event-type-info';
import EventContributorRoleInfo, {
  CREATE_EVENT_NO_ROLE_VALUE,
  type CreateEventRoleFieldValue,
} from '@/components/common/event-contributor-role-info';
import CollapsibleInfoAlert from '@/components/common/collapsible-info-alert';
import { MAX_EVENT_DAYS } from '@/constants';
import { useNavigate } from 'react-router';
import { useEffect } from 'react';
import TimePicker from '@/components/common/time-picker';
import { format, isAfter, isBefore, parse, startOfDay } from 'date-fns';
import { useFeatureAccess } from '@/hooks/use-feature-access';
import { usePermissionsQuery } from '@/state/queries/permissions/use-permissions';
import { getLatestAllowedEventStartDate } from '@/utils/event-create-date-bounds';
import { EVENT_END_TIME_MAX, EVENT_START_TIME_MAX, EVENT_START_TIME_MIN } from '@/utils/event-day-time-bounds';

const EventForm = ({ onClose, onSuccess }: { onClose: () => void; onSuccess: () => void }) => {
  const t = useTranslations();
  const notification = useNotifications();
  const navigate = useNavigate();
  const activeOrganization = useActiveOrganizationStore((s) => s.activeOrganization);
  const { hasAccess: hasMultiFlowAccess } = useFeatureAccess('multi-flow');
  const { data: permissions } = usePermissionsQuery();

  const isOrgAdmin = !!(
    activeOrganization?.id && permissions?.organizations?.[activeOrganization.id]?.includes('update')
  );

  const createEventSchema = createEventSchemaFactory(t);
  type EventFormType = z.infer<typeof createEventSchema>;
  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<EventFormType>({
    resolver: zodResolver(createEventSchema),
    defaultValues: {
      type: '',
      title: '',
      durationDays: 1,
      startDate: new Date(),
      multiFlow: false,
      role: 'owner',
      startTime: '09:00',
      endTime: undefined,
    },
  });

  const durationDays = useWatch({ control, name: 'durationDays', defaultValue: 1 });
  const multiFlow = useWatch({ control, name: 'multiFlow', defaultValue: false });
  const startTime = useWatch({ control, name: 'startTime', defaultValue: '09:00' });
  const endTime = useWatch({ control, name: 'endTime' });
  const startDate = useWatch({ control, name: 'startDate', defaultValue: new Date() });

  useEffect(() => {
    if (durationDays > 1 && !multiFlow) {
      setValue('multiFlow', true);
    }
  }, [durationDays, multiFlow, setValue]);

  useEffect(() => {
    if (!multiFlow && durationDays > 1) {
      setValue('durationDays', 1);
    }
  }, [multiFlow, durationDays, setValue]);

  useEffect(() => {
    // Clear startTime and endTime when durationDays is not 1, or set default when it becomes 1
    if (durationDays !== 1) {
      setValue('startTime', undefined);
      setValue('endTime', undefined);
    } else if (durationDays === 1 && !startTime) {
      setValue('startTime', '09:00');
    }
  }, [durationDays, startTime, setValue]);

  useEffect(() => {
    if (durationDays !== 1 || !startTime || !endTime) {
      return;
    }
    const ref = startDate instanceof Date ? startDate : new Date(startDate);
    const start = parse(startTime, 'HH:mm', ref);
    const end = parse(endTime, 'HH:mm', ref);
    if (isBefore(end, start)) {
      setValue('endTime', undefined);
    }
  }, [durationDays, startTime, endTime, startDate, setValue]);

  useEffect(() => {
    const ref = new Date();
    const maxStart = getLatestAllowedEventStartDate(durationDays, ref);
    const sd = startDate instanceof Date ? startDate : new Date(startDate);
    if (isAfter(startOfDay(sd), startOfDay(maxStart))) {
      setValue('startDate', maxStart);
    }
  }, [durationDays, startDate, setValue]);

  const { mutateAsync, isPending } = useCreateEventMutation();

  const onSubmit = async (data: EventFormType) => {
    if (!activeOrganization) {
      notification.notifyError(t('event.notification.missingOrganization'));
      return;
    }

    const { role, startTime, endTime, ...restData } = data;
    try {
      const eventData = await mutateAsync({
        ...restData,
        role: role && role !== CREATE_EVENT_NO_ROLE_VALUE ? role : undefined,
        ownedById: activeOrganization.id,
        // The venue is usually where the event is planned; the schedule settings can change it.
        timezone: browserTimeZone(),
        // Only include startTime and endTime for 1-day events
        ...(data.durationDays === 1
          ? {
              ...(startTime ? { startTime } : {}),
              ...(endTime ? { endTime } : {}),
            }
          : {}),
      });
      if (eventData) {
        onSuccess();
        onClose();
        notification.notifySuccess(t('event.notification.createSuccess', { title: eventData.title }));
        navigate(`/event/${eventData.id}`);
      }
    } catch {
      notification.notifyError(t('event.notification.createError'));
    }
  };

  return (
    <Modal
      open={true}
      size="sm"
      handleClose={onClose}
      title={t('event.modalTitle')}
      description={t('event.modalDescription')}
      footer={
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="outlined" size="small" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="contained" size="small" loading={isPending} onClick={handleSubmit(onSubmit)}>
            {t('common.create')}
          </Button>
        </Box>
      }
    >
      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          width: '100%',
          margin: 0,
        }}
      >
        <Controller
          name="title"
          control={control}
          render={({ field }) => (
            <Box sx={{ width: '100%' }}>
              <TextField
                {...field}
                fullWidth
                variant="standard"
                label={t('event.eventTitle')}
                placeholder={t('event.titlePlaceholder')}
                error={!!errors.title}
                helperText={errors.title?.message}
              />
            </Box>
          )}
        />
        <Controller
          name="type"
          control={control}
          render={({ field }) => (
            <Box sx={{ width: '100%' }}>
              <FormControl fullWidth variant="standard" error={!!errors.type}>
                <InputLabel id="create-event-type-label" shrink>
                  {t('event.eventType')}
                </InputLabel>
                <Select
                  {...field}
                  labelId="create-event-type-label"
                  label={t('event.eventType')}
                  displayEmpty
                  renderValue={(value) => {
                    const selected = value as EventType | '';
                    if (!selected) {
                      return (
                        <Typography variant="body2" color="text.primary">
                          {t('event.selectType')}
                        </Typography>
                      );
                    }
                    return <EventTypeInfo type={selected} showDescription={false} />;
                  }}
                  sx={{
                    '& .MuiSelect-select': {
                      display: 'flex',
                      alignItems: 'center',
                      py: 0.5,
                      pr: '28px !important',
                    },
                  }}
                >
                  <MenuItem value="" disabled>
                    {t('event.selectType')}
                  </MenuItem>
                  {Object.keys(EVENT_TYPE).map((type) => (
                    <MenuItem key={type} value={type}>
                      <EventTypeInfo type={type as EventType} />
                    </MenuItem>
                  ))}
                </Select>
                {errors.type && (
                  <Typography variant="caption" color="error">
                    {errors.type.message}
                  </Typography>
                )}
              </FormControl>
            </Box>
          )}
        />

        <Controller
          name="role"
          control={control}
          render={({ field }) => (
            <Box sx={{ width: '100%' }}>
              <FormControl fullWidth variant="standard" error={!!errors.role}>
                <InputLabel>{t('event.roleInEvent')}</InputLabel>
                <Select
                  {...field}
                  disabled={!isOrgAdmin}
                  renderValue={(value) => (
                    <EventContributorRoleInfo value={value as CreateEventRoleFieldValue} showDescription={false} />
                  )}
                  sx={{
                    '& .MuiSelect-select': {
                      display: 'flex',
                      alignItems: 'center',
                      py: 0.5,
                      pr: '28px !important',
                    },
                  }}
                >
                  {isOrgAdmin && (
                    <MenuItem value={CREATE_EVENT_NO_ROLE_VALUE}>
                      <EventContributorRoleInfo value={CREATE_EVENT_NO_ROLE_VALUE} />
                    </MenuItem>
                  )}
                  {EVENT_CONTRIBUTORS_ROLES.map((role: EventContributorRole) => (
                    <MenuItem key={role} value={role}>
                      <EventContributorRoleInfo value={role} />
                    </MenuItem>
                  ))}
                </Select>
                {errors.role && (
                  <Typography variant="caption" color="error">
                    {errors.role.message}
                  </Typography>
                )}
              </FormControl>
            </Box>
          )}
        />

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Controller
            name="startDate"
            control={control}
            render={({ field }) => {
              const policyRef = new Date();
              const maxStart = getLatestAllowedEventStartDate(durationDays, policyRef);
              return (
                <Box sx={{ maxWidth: '130px' }}>
                  <DatePicker
                    {...field}
                    label={t('event.startDate')}
                    minDate={new Date()}
                    maxDate={maxStart}
                    slotProps={{
                      textField: {
                        variant: 'standard',
                        fullWidth: true,
                        error: !!errors.startDate,
                        helperText: errors.startDate?.message,
                      },
                    }}
                  />
                </Box>
              );
            }}
          />

          {hasMultiFlowAccess && (
            <Controller
              name="durationDays"
              control={control}
              render={({ field }) => (
                <FormControl sx={{ maxWidth: '120px' }} variant="standard" error={!!errors.durationDays}>
                  <InputLabel>{t('event.durationDays')}</InputLabel>
                  <Select {...field} value={field.value || 1}>
                    {Array.from({ length: MAX_EVENT_DAYS }, (_, i) => i + 1).map((day) => (
                      <MenuItem key={day} value={day}>
                        {day} {day === 1 ? t('common.day') : t('common.days')}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.durationDays && (
                    <Typography variant="caption" color="error">
                      {errors.durationDays.message}
                    </Typography>
                  )}
                </FormControl>
              )}
            />
          )}

          {hasMultiFlowAccess && (
            <Controller
              name="multiFlow"
              control={control}
              render={({ field }) => (
                <Stack alignItems="center">
                  <Typography variant="caption" sx={{ fontSize: '0.875rem', display: 'block', marginBottom: 0.5 }}>
                    {t('event.multiFlow')}
                  </Typography>
                  <Switch checked={field.value} onChange={(e) => field.onChange(e.target.checked)} size="small" />
                </Stack>
              )}
            />
          )}
        </Box>

        {durationDays === 1 && (
          <Stack direction="row" spacing={1} alignItems="flex-start" width="100%">
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Controller
                name="startTime"
                control={control}
                render={({ field: { value, onChange } }) => {
                  const startTimeLabel = `${t('event.detail.addFlow.values.startTime')} *`;
                  const ref = startDate instanceof Date ? startDate : new Date(startDate);
                  const minStart = parse(EVENT_START_TIME_MIN, 'HH:mm', ref);
                  const maxStart = parse(EVENT_START_TIME_MAX, 'HH:mm', ref);
                  return (
                    <TimePicker
                      label={startTimeLabel}
                      name="startTime"
                      views={['hours', 'minutes']}
                      minTime={minStart}
                      maxTime={maxStart}
                      value={value ? parse(value, 'HH:mm', ref) : null}
                      onChange={(value) => {
                        if (value) {
                          onChange(format(value, 'HH:mm'));
                        } else {
                          onChange(undefined);
                        }
                      }}
                      slotProps={{
                        textField: {
                          variant: 'standard',
                          required: true,
                          error: !!errors.startTime,
                          helperText: errors.startTime?.message,
                          fullWidth: true,
                          sx: { minWidth: 0 },
                        },
                      }}
                    />
                  );
                }}
              />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Controller
                name="endTime"
                control={control}
                render={({ field: { value, onChange } }) => {
                  const ref = startDate instanceof Date ? startDate : new Date(startDate);
                  const minTime = startTime ? parse(startTime, 'HH:mm', ref) : undefined;
                  const maxEnd = parse(EVENT_END_TIME_MAX, 'HH:mm', ref);
                  return (
                    <TimePicker
                      label={t('event.detail.addFlow.values.endTime')}
                      name="endTime"
                      views={['hours', 'minutes']}
                      minTime={minTime}
                      maxTime={maxEnd}
                      value={value ? parse(value, 'HH:mm', ref) : null}
                      onChange={(value) => {
                        if (value) {
                          onChange(format(value, 'HH:mm'));
                        } else {
                          onChange(undefined);
                        }
                      }}
                      slotProps={{
                        textField: {
                          variant: 'standard',
                          required: false,
                          error: !!errors.endTime,
                          helperText: errors.endTime?.message,
                          fullWidth: true,
                          sx: { minWidth: 0 },
                          placeholder: 'flexible',
                        },
                      }}
                    />
                  );
                }}
              />
            </Box>
          </Stack>
        )}

        {hasMultiFlowAccess && (
          <CollapsibleInfoAlert
            title={t('event.multiFlowInfo.title')}
            description={t('event.multiFlowInfo.description')}
          />
        )}
      </Box>
    </Modal>
  );
};

export default EventForm;
